import { z } from 'zod'

import type { DesktopBridge } from './desktop-auth'

const LOCAL_ZERO_CACHE_URL = 'http://localhost:4848'
const PRODUCTION_ZERO_CACHE_URL = 'https://zero.trevize.dev'

function isLocalHostname(origin: string): boolean {
  try {
    const hostname = new URL(origin).hostname
    return hostname === 'localhost' || hostname === '127.0.0.1'
  } catch {
    return false
  }
}

function emptyToUndefined(value: string | undefined): string | undefined {
  if (value === undefined || value.trim() === '') return undefined
  return value
}

function parseUrl(value: string, ctx: z.RefinementCtx): URL | undefined {
  try {
    return new URL(value)
  } catch {
    ctx.addIssue({ code: 'custom', message: 'must be a valid URL' })
    return undefined
  }
}

function isCredentialFreeHttp(url: URL): boolean {
  return (
    (url.protocol === 'http:' || url.protocol === 'https:') &&
    !url.username &&
    !url.password &&
    !url.search &&
    !url.hash
  )
}

const httpOriginSchema = z.string().transform((value, ctx) => {
  const url = parseUrl(value, ctx)
  if (!url) return z.NEVER
  if (
    !isCredentialFreeHttp(url) ||
    (url.pathname !== '/' && url.pathname !== '')
  ) {
    ctx.addIssue({ code: 'custom', message: 'must be an http(s) origin' })
    return z.NEVER
  }
  return url.origin
})

const httpUrlSchema = z.string().transform((value, ctx) => {
  const url = parseUrl(value, ctx)
  if (!url) return z.NEVER
  if (!isCredentialFreeHttp(url)) {
    ctx.addIssue({ code: 'custom', message: 'must be an http(s) URL' })
    return z.NEVER
  }
  url.pathname = url.pathname.replace(/\/+$/, '') || '/'
  return url.toString().replace(/\/$/, url.pathname === '/' ? '' : '/')
})

const previewSchema = z.object({
  scheme: z.enum(['http', 'https']).optional(),
  port: z
    .string()
    .regex(/^\d{1,5}$/)
    .refine((port) => {
      const number = Number(port)
      return Number.isInteger(number) && number >= 1 && number <= 65535
    })
    .optional(),
})

const runtimeConfigSchema = z.object({
  applicationOrigin: httpOriginSchema,
  apiOrigin: httpOriginSchema,
  authOrigin: httpOriginSchema,
  zeroCacheUrl: httpOriginSchema.optional(),
  zeroQueryUrl: httpUrlSchema,
  zeroMutateUrl: httpUrlSchema,
  preview: previewSchema.default({}),
  packagedClientDeploymentId: z.string().trim().min(1).optional(),
})

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>

export type RuntimeConfigOverrides = {
  apiOrigin?: string
  authOrigin?: string
  zeroCacheUrl?: string
  zeroQueryUrl?: string
  zeroMutateUrl?: string
  preview?: { scheme?: string; port?: string }
  development?: boolean
  packagedClientDeploymentId?: string
}

declare global {
  interface Window {
    __TREVIZE_RUNTIME_CONFIG__?: unknown
    trevize?: DesktopBridge
  }
}

function parseOrThrow<T>(
  schema: z.ZodType<T>,
  input: unknown,
  fallbackPath: string,
): T {
  const result = schema.safeParse(input)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const path =
    issue && issue.path.length > 0
      ? issue.path.map(String).join('.')
      : fallbackPath
  throw new Error(`Trevize runtime configuration has an invalid ${path}.`)
}

export function parseRuntimeConfig(input: unknown): RuntimeConfig {
  return parseOrThrow(runtimeConfigSchema, input, 'value')
}

function defaultZeroCacheUrl(
  origin: string,
  options: { explicit?: string; development?: boolean } = {},
): string {
  if (options.explicit) return options.explicit
  if (isLocalHostname(origin) || options.development)
    return LOCAL_ZERO_CACHE_URL
  return PRODUCTION_ZERO_CACHE_URL
}

export function runtimeConfigFromOrigin(
  origin: string,
  overrides: RuntimeConfigOverrides = {},
): RuntimeConfig {
  const applicationOrigin = parseOrThrow(
    httpOriginSchema,
    origin,
    'applicationOrigin',
  )
  const apiOrigin = parseOrThrow(
    httpOriginSchema,
    overrides.apiOrigin ?? applicationOrigin,
    'apiOrigin',
  )
  return parseRuntimeConfig({
    applicationOrigin,
    apiOrigin,
    authOrigin: overrides.authOrigin ?? apiOrigin,
    zeroCacheUrl: defaultZeroCacheUrl(apiOrigin, {
      explicit: overrides.zeroCacheUrl,
      development: overrides.development,
    }),
    zeroQueryUrl: overrides.zeroQueryUrl ?? `${apiOrigin}/api/zero/query`,
    zeroMutateUrl: overrides.zeroMutateUrl ?? `${apiOrigin}/api/zero/mutate`,
    preview: overrides.preview ?? {},
    ...(overrides.packagedClientDeploymentId
      ? { packagedClientDeploymentId: overrides.packagedClientDeploymentId }
      : {}),
  })
}

function resolvePageOrigin(): string {
  if (typeof window !== 'undefined') return window.location.origin
  return (
    emptyToUndefined(import.meta.env.VITE_APPLICATION_ORIGIN) ??
    `http://localhost:${process.env.PORT ?? 3000}`
  )
}

let cachedConfig: RuntimeConfig | undefined

export function getRuntimeConfig(): RuntimeConfig {
  if (cachedConfig) return cachedConfig
  const injected =
    typeof window !== 'undefined'
      ? window.__TREVIZE_RUNTIME_CONFIG__
      : undefined
  cachedConfig =
    injected === undefined
      ? runtimeConfigFromOrigin(resolvePageOrigin(), {
          zeroCacheUrl: emptyToUndefined(import.meta.env.VITE_ZERO_CACHE_URL),
          preview: {
            scheme: emptyToUndefined(import.meta.env.VITE_PREVIEW_SCHEME),
            port: emptyToUndefined(import.meta.env.VITE_PREVIEW_PORT),
          },
          development: import.meta.env.DEV,
        })
      : parseRuntimeConfig(injected)
  return cachedConfig
}

export function resetRuntimeConfigForTests(): void {
  cachedConfig = undefined
}
